/**
 * Deep-linking a single photo inside an album.
 *
 * The FR Diagnostics page samples photos by id, but a bare photo id is an
 * internal primary key that appears on no camp-facing surface — it identifies
 * nothing to a human. Linking each sampled photo back to its album (scrolled to
 * and highlighted) is what makes it reviewable.
 *
 * Kept in its own module so the builder, the query-param name and the DOM
 * anchor id can never drift apart across the linking page, the gallery card
 * that renders the anchor, and the hook that consumes the param. Lives in
 * `utils/` rather than under `views/AlbumView/` because the shared
 * PhotoGallery component consumes it — a shared component reaching up into a
 * sibling view's folder inverts the dependency direction.
 *
 * Ids are `number` throughout. The two inputs that aren't natively numeric —
 * the gallery's string photo key and the raw `?photo=` param — are normalized
 * by their callers (`parsePhotoParam` below, and `Number()` in
 * GalleryImageCard) so the id's canonical type is pinned here rather than
 * absorbed by a `number | string` union at every signature.
 */

export const PHOTO_QUERY_PARAM = 'photo'

/**
 * DOM id stamped on a gallery card so the deep link has something to scroll to.
 * Prefixed because a bare numeric id is not a valid target for
 * `document.getElementById` lookups we control, and an unprefixed number risks
 * colliding with any other id on the page.
 */
export const photoAnchorId = (photoId: number): string =>
  `album-photo-${photoId}`

/** `/album/123?photo=456` — the album route, focused on one photo. */
export const buildAlbumPhotoUrl = (albumId: number, photoId: number): string =>
  `/album/${albumId}?${PHOTO_QUERY_PARAM}=${photoId}`

/**
 * Reads `?photo=` out of a query string as a positive integer.
 *
 * Returns null for absent, non-numeric, zero, negative and fractional values.
 * Rejecting rather than coercing matters: `Number('abc')` is NaN and
 * `photoAnchorId(NaN)` would look up `album-photo-NaN`, which is a lookup that
 * can only ever miss but reads like a real one at the call site.
 */
export const parsePhotoParam = (search: string): number | null => {
  const raw = new URLSearchParams(search).get(PHOTO_QUERY_PARAM)
  if (!raw) return null

  const photoId = Number(raw)
  return Number.isInteger(photoId) && photoId > 0 ? photoId : null
}

/**
 * Whether an id is usable as a link target.
 *
 * The server can legitimately send 0 for an album id (see the decode layer's
 * coercion), which would otherwise build `/album/0?photo=…` — a link that
 * navigates the operator somewhere useless. Callers render unlinked instead.
 */
export const isLinkableId = (id: number): boolean =>
  Number.isInteger(id) && id > 0
